/**
 * useGeneratorNode - Hook for managing AI generation in GeneratorNode
 *
 * Handles:
 * - Finding downstream target BlockNode
 * - Calling /api/boards/:boardId/generate with SSE
 * - Streaming response chunks
 * - Appending to target BlockNode's Yjs document
 * - Status management (idle/generating/complete/error)
 *
 * Usage:
 * ```tsx
 * const { generate, status, error } = useGeneratorNode({
 *   generatorNodeId,
 *   generatorData,
 *   boardId,
 *   edges,
 *   nodesMap,
 *   ydoc,
 * });
 * ```
 */

import { useState, useCallback } from 'react';
import type { Edge, Node } from '@xyflow/react';
import * as Y from 'yjs';
import type { GeneratorNodeData, GenerationStatus } from '@deep-agents/shared-types';
import { getNodeContent, getNodeLabel } from '../utils/nodeContent';

// ============================================================================
// TYPES
// ============================================================================

interface UseGeneratorNodeProps {
  /** ID of the generator node */
  generatorNodeId: string;

  /** Generator node data (prompt, model, etc.) */
  generatorData: GeneratorNodeData;

  /** Board ID for API call */
  boardId: string;

  /** All edges in the flow (to find downstream BlockNode) */
  edges: Edge[];

  /** Yjs map of all nodes (to find target BlockNode) */
  nodesMap: Y.Map<Node>;

  /** Yjs document for accessing BlockNode's XmlFragment */
  ydoc: Y.Doc;
}

interface UseGeneratorNodeResult {
  /** Trigger generation */
  generate: () => Promise<void>;

  /** Current generation status */
  status: GenerationStatus;

  /** Error message if generation failed */
  error: string | null;
}

// ============================================================================
// HOOK
// ============================================================================

export function useGeneratorNode({
  generatorNodeId,
  generatorData,
  boardId,
  edges,
  nodesMap,
  ydoc,
}: UseGeneratorNodeProps): UseGeneratorNodeResult {
  const [status, setStatus] = useState<GenerationStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  /**
   * Find the downstream BlockNode connected to this GeneratorNode.
   * Returns the BlockNode ID or null if not found.
   */
  const findTargetBlockNode = useCallback((): string | null => {
    // Find edge going FROM this generator node
    const outgoingEdge = edges.find((edge) => edge.source === generatorNodeId);

    if (!outgoingEdge) {
      return null;
    }

    // Get the target node
    const targetNode = nodesMap.get(outgoingEdge.target);

    // Verify it's a BlockNode
    if (targetNode && targetNode.type === 'block') {
      return targetNode.id;
    }

    return null;
  }, [generatorNodeId, edges, nodesMap, ydoc]);

  /**
   * Find and extract content from upstream BlockNodes.
   * Returns an array of connections with text content.
   */
  const extractUpstreamContext = useCallback((): Array<{
    nodeId: string;
    nodeLabel: string;
    nodeType: string;
    textContent: string;
  }> => {
    // Find all edges coming INTO this generator node
    const incomingEdges = edges.filter((edge) => edge.target === generatorNodeId);

    const connections = [];

    for (const edge of incomingEdges) {
      // Get the source node (upstream)
      const sourceNode = nodesMap.get(edge.source);

      if (!sourceNode) {
        continue;
      }

      const { textContent } = getNodeContent(sourceNode, ydoc);

      connections.push({
        nodeId: sourceNode.id,
        nodeLabel: getNodeLabel(sourceNode),
        nodeType: sourceNode.type ?? 'unknown',
        textContent: textContent ?? '',
      });
    }

    return connections;
  }, [generatorNodeId, edges, nodesMap]);

  /**
   * Append text to the target BlockNode's Yjs document.
   * Uses per-node fragment (e.g., 'richtext-nodeId') to match RichTextEditor.
   */
  type StreamPart =
    | { type: 'text'; value: string }
    | { type: 'mention'; id: string; label: string };

  const appendPartsToBlockNode = useCallback((targetBlockId: string, parts: StreamPart[]) => {
    if (parts.length === 0) return;
    // Get the unique richtext fragment for this specific BlockNode
    // This matches the fieldName passed to RichTextEditor in BlockNode.tsx
    const fragment = ydoc.getXmlFragment(`richtext-${targetBlockId}`);

    // Insert text at the end
    // Yjs XmlFragment represents ProseMirror/Tiptap document structure
    // We need to insert into the last paragraph or create a new text node

    // Get the last child (should be a paragraph)
    const lastChild = fragment.get(fragment.length - 1);

    // Check if lastChild is an XmlElement paragraph
    let paragraph: Y.XmlElement | undefined;

    if (lastChild instanceof Y.XmlElement && lastChild.nodeName === 'paragraph') {
      paragraph = lastChild;
    } else {
      paragraph = new Y.XmlElement('paragraph');
      fragment.push([paragraph]);
    }

    parts.forEach((part) => {
      if (part.type === 'text') {
        const textNode = new Y.XmlText();
        textNode.insert(0, part.value);
        paragraph!.push([textNode]);
      } else {
        const mentionNode = new Y.XmlElement('mention');
        mentionNode.setAttribute('id', part.id);
        mentionNode.setAttribute('label', part.label);
        paragraph!.push([mentionNode]);
      }
    });
  }, [ydoc]);

  /**
   * Clear all content in the target BlockNode before streaming new output.
   * This avoids duplicated outputs across repeated generations.
   */
  const clearBlockNode = useCallback((targetBlockId: string) => {
    const fragment = ydoc.getXmlFragment(`richtext-${targetBlockId}`);
    if (fragment.length === 0) return;
    fragment.delete(0, fragment.length);
  }, [ydoc]);

  /**
   * Generate content by calling the API and streaming to target BlockNode.
   */
  const generate = useCallback(async () => {
    console.log('[useGeneratorNode] Generate called');

    // Find target BlockNode
    const targetBlockId = findTargetBlockNode();
    console.log('[useGeneratorNode] Target block ID:', targetBlockId);

    if (!targetBlockId) {
      console.error('[useGeneratorNode] No target block found');
      setStatus('error');
      setError('No downstream BlockNode connected. Please connect this generator to a Block node.');
      return;
    }

    // Extract upstream context from connected BlockNodes
    const upstreamConnections = extractUpstreamContext();
    console.log('[useGeneratorNode] Upstream connections:', upstreamConnections);

    const enrichedGeneratorData = {
      ...generatorData,
      prompt: generatorData.prompt || '',
      incomingConnections: upstreamConnections,
    };
    console.log('[useGeneratorNode] Enriched data:', enrichedGeneratorData);

    // Reset state
    setStatus('generating');
    setError(null);

    try {
      let accumulatedOutput = '';

      const updateGeneratorOutput = (nextOutput: string) => {
        const existing = nodesMap.get(generatorNodeId);
        if (!existing) return;
        ydoc.transact(() => {
          nodesMap.set(generatorNodeId, {
            ...existing,
            data: {
              ...existing.data,
              lastOutput: nextOutput,
            },
          });
        });
      };

      clearBlockNode(targetBlockId);
      updateGeneratorOutput('');

      const labelById = new Map(
        upstreamConnections.map((conn) => [conn.nodeId, conn.nodeLabel || conn.nodeId])
      );
      const contentById = new Map(
        upstreamConnections.map((conn) => [conn.nodeId, conn.textContent || ''])
      );
      let pendingRaw = '';

      const resolveTokens = (text: string) => {
        let result = text;
        for (const [nodeId, content] of contentById) {
          if (!content) continue;
          const token = `@{{${nodeId}}}`;
          if (result.includes(token)) {
            result = result.split(token).join(content);
          }
        }
        return result;
      };

      const parseMentionParts = (text: string): StreamPart[] => {
        if (!text) return [];
        const parts: StreamPart[] = [];
        const regex = /@{{([^}]+)}}/g;
        let lastIndex = 0;
        let match: RegExpExecArray | null;
        while ((match = regex.exec(text)) !== null) {
          const before = text.slice(lastIndex, match.index);
          if (before) {
            parts.push({ type: 'text', value: before });
          }
          const nodeId = match[1];
          const label = labelById.get(nodeId);
          if (label) {
            parts.push({ type: 'mention', id: nodeId, label });
          } else {
            parts.push({ type: 'text', value: match[0] });
          }
          lastIndex = regex.lastIndex;
        }
        const rest = text.slice(lastIndex);
        if (rest) {
          parts.push({ type: 'text', value: rest });
        }
        return parts;
      };

      const createNodesFromParts = (parts: StreamPart[]) => {
        const nodes: Array<Y.XmlText | Y.XmlElement> = [];
        parts.forEach((part) => {
          if (part.type === 'text') {
            const textNode = new Y.XmlText();
            textNode.insert(0, part.value);
            nodes.push(textNode);
          } else {
            const mentionNode = new Y.XmlElement('mention');
            mentionNode.setAttribute('id', part.id);
            mentionNode.setAttribute('label', part.label);
            nodes.push(mentionNode);
          }
        });
        return nodes;
      };

      const replaceTokensWithMentions = () => {
        const fragment = ydoc.getXmlFragment(`richtext-${targetBlockId}`);
        const walk = (parent: Y.XmlFragment | Y.XmlElement) => {
          for (let i = 0; i < parent.length; i += 1) {
            const child = parent.get(i);
            if (child instanceof Y.XmlText) {
              const text = child.toString();
              if (!text.includes('@{{')) {
                continue;
              }
              const parts = parseMentionParts(text);
              const hasMention = parts.some((part) => part.type === 'mention');
              if (!hasMention) {
                continue;
              }
              const nodes = createNodesFromParts(parts);
              parent.delete(i, 1);
              if (nodes.length > 0) {
                parent.insert(i, nodes);
                i += nodes.length - 1;
              } else {
                i -= 1;
              }
              continue;
            }
            if (child instanceof Y.XmlElement) {
              walk(child);
            }
          }
        };
        ydoc.transact(() => {
          walk(fragment);
        });
      };

      const appendWithMentions = (text: string) => {
        const parts = parseMentionParts(text);
        if (parts.length === 0) return;
        appendPartsToBlockNode(targetBlockId, parts);
      };

      // Call the generate API with SSE
      console.log('[useGeneratorNode] Calling API:', `/api/boards/${boardId}/generate`);
      const response = await fetch(`/api/boards/${boardId}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          generatorNodeData: enrichedGeneratorData,
          targetBlockId,
        }),
      });
      console.log('[useGeneratorNode] Response status:', response.status, response.statusText);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      if (!response.body) {
        throw new Error('No response body');
      }

      // Read SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        // Decode chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE messages (terminated by \n\n)
        const messages = buffer.split('\n\n');

        // Keep last incomplete message in buffer
        buffer = messages.pop() || '';

        for (const message of messages) {
          if (!message.trim()) continue;

          // Parse SSE message format: "data: {...}"
          const dataPrefix = 'data: ';
          if (!message.startsWith(dataPrefix)) continue;

          const dataStr = message.slice(dataPrefix.length);

          // Check for [DONE] signal
          if (dataStr === '[DONE]') {
            setStatus('complete');
            return;
          }

          // Parse JSON chunk
          try {
            const chunk = JSON.parse(dataStr);

            if (chunk.type === 'text' && chunk.content) {
              const combined = pendingRaw + chunk.content;
              const lastOpen = combined.lastIndexOf('@{{');
              const lastClose = combined.lastIndexOf('}}');

              let flushText = combined;
              if (lastOpen !== -1 && lastClose < lastOpen) {
                pendingRaw = combined.slice(lastOpen);
                flushText = combined.slice(0, lastOpen);
              } else {
                pendingRaw = '';
              }

              if (flushText) {
                appendWithMentions(flushText);
                accumulatedOutput += resolveTokens(flushText);
                updateGeneratorOutput(accumulatedOutput);
              }
            } else if (chunk.type === 'error') {
              setStatus('error');
              setError(chunk.content || 'Unknown error');
              return;
            }
          } catch (parseError) {
            console.error('Failed to parse SSE chunk:', parseError, dataStr);
          }
        }
      }

      if (pendingRaw) {
        appendWithMentions(pendingRaw);
        accumulatedOutput += resolveTokens(pendingRaw);
        updateGeneratorOutput(accumulatedOutput);
        pendingRaw = '';
      }

      replaceTokensWithMentions();

      setStatus('complete');
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    }
  }, [
    generatorData,
    boardId,
    findTargetBlockNode,
    extractUpstreamContext,
    appendPartsToBlockNode,
    clearBlockNode,
    nodesMap,
    ydoc,
    generatorNodeId,
  ]);

  return {
    generate,
    status,
    error,
  };
}
